import React, {
  useEffect, useRef, useState,
} from 'react';
import {
  Form, Input, InputNumber, message, Modal, Tooltip,
} from 'antd';
import { ProTable } from '@ant-design/pro-components';
import { ActionType, ProColumns } from '@ant-design/pro-table/es/typing';
import {
  CheckOutlined, CloseOutlined, LoadingOutlined, PlusOutlined,
} from '@ant-design/icons';
import { Rule } from 'rc-field-form/lib/interface';
import clsx from 'clsx';
import TextArea from 'antd/es/input/TextArea';
import { OrderInputProps } from '../Input';
import { FormName, useOrderContextForm } from '../../context';
import { ClientDataGood, ClientDataPackages, PackageItem } from '../../../Adapter';
import { useSimpleModal } from '../../../../../Common/Modal/Simple';
import { IconDangerousOutlined } from '../../../../../Common/Icon';

import styles from '../../index.module.scss';

const countGoodsItemOccurrences = (
  packages: ClientDataPackages[],
  goodsItemId: string,
): number => (packages || []).reduce((total, pkg) => (
  total
      + (pkg.items || []).reduce((itemTotal, item) => {
        if (item.goodsItem.id === goodsItemId) {
          return itemTotal + item.quantity;
        }

        return itemTotal;
      }, 0)
), 0);

interface InputSelectGoodForPackageProps extends OrderInputProps, FormName {
  item: ClientDataPackages;
  index: number;
  validationRules: Rule;
}

export default function InputSelectGoodForPackage({
  formName,
  index,
  ...props
}: InputSelectGoodForPackageProps) {
  const actionRef = useRef<ActionType | undefined>();
  const {
    forms: { [formName]: form }, triggerValidationAllForm, goodsForm,
    orderUpdate,
  } = useOrderContextForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<PackageItem[]>([]);
  const packagesFormWatch: ClientDataPackages[] = Form.useWatch('packages', form) || [];

  const inputValueWatch: PackageItem[] = Form.useWatch([formName, index, 'items']);

  /** Products form */
  const [dataSource, setDataSource] = useState<ClientDataGood[]>([]);

  useEffect(() => {
    if (isModalOpen) {
      setDataSource(goodsForm.getFieldsValue()?.goods || []);
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (dataSource) {
      const productsValue = form.getFieldValue([formName, index, 'items']);

      setSelectedProducts(productsValue || []);
    }
  }, [dataSource]);

  const handleSelectedProducts = (quantity: number | null, entity: ClientDataGood) => {
    const { id } = entity;

    if (quantity) {
      if (id) {
        setSelectedProducts((prevState) => {
          const currentItem = prevState?.find((i) => (i?.goodsItem?.id === id));

          /** If item already was added - then update it (to keep same ID) */
          if (currentItem) {
            return [
              ...(prevState || []).filter((i) => i?.goodsItem?.id !== id),
              { ...currentItem, quantity },
            ];
          }

          /** Else create new item */
          return [
          /** If on select we already have some selected items - ignore they on new 'add' action  */
            ...prevState, // .filter((i) => i?.goodsItem?.id !== id),
            {
              // id: i.id || undefined,
              quantity,
              goodsItem: entity,
            },
          ];
        });
      } else {
        message.error('Goods item has no ID. Save the goods and then try adding again.', 10);
      }
    } else {
      /** If no quantity remove this item from selected products */
      setSelectedProducts((prevState) => prevState
        .filter((pack) => pack?.goodsItem?.id !== id));
    }
  };

  const columns: ProColumns<ClientDataGood>[] = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Kind of Package',
      dataIndex: 'packagingGroup',
      key: 'packagingGroup',
      width: '200px',
    },
    {
      title: 'Weight',
      dataIndex: 'gross',
      key: 'gross',
      renderText: (text) => (text ? `${text} kg` : ''),
    },
    {
      title: 'Type',
      dataIndex: 'dangerousGoods',
      key: 'dangerousGoods',
      renderText: (_, entity) => (
        entity.dangerousGoods ? (
          <span title="Dangerous goods" style={{ fontSize: '24px' }}><IconDangerousOutlined /></span>
        ) : null
      ),
    },
    {
      title: 'Packed',
      dataIndex: 'packed',
      key: 'packed',
      renderText: (_, entity) => {
        const countOccurrences: number = countGoodsItemOccurrences(packagesFormWatch, entity?.id || '');

        if (countOccurrences && countOccurrences >= entity.quantity) {
          return <CheckOutlined style={{ color: '#52C41A' }} title="All items packed" />;
        }

        if (countOccurrences >= 1 && countOccurrences <= entity.quantity) {
          return <CheckOutlined style={{ color: '#FAAD14' }} title="Partially packed" />;
        }

        return null;
      },
    },
    {
      title: 'Total',
      dataIndex: 'quantity',
      key: 'quantity',
      renderText: (_, entity) => (entity.quantity),
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: '120px',
      render: (_, entity) => (
        <div
          key={entity?.id}
          role="button"
          tabIndex={-1}
          aria-label="input-holder"
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
        >
          <InputNumber
            min={0}
            placeholder="0"
            precision={0}
            defaultValue={selectedProducts.find((pack) => pack?.goodsItem?.id === entity.id)?.quantity}
            onChange={(value) => handleSelectedProducts(value, entity)}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
          />
        </div>
      ),
    },
  ];

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    setIsModalOpen(false);
    // setSelectedProducts([]);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    // setSelectedProducts([]);
  };

  const handleSideCalcActions = (selectedItems: PackageItem[]) => {
    /** Update weight after update of package */
    const updatedWeight = selectedItems
      .reduce((total, curr) => (
        total + ((curr.goodsItem?.gross || 0) * (curr?.quantity || 0))
      ), 0)
      .toFixed(3);

    form.setFieldValue([formName, index, 'weight'], Number.parseFloat(updatedWeight));

    /** Update pricing */
    const updatedValue = selectedItems
      .reduce((total, curr) => (
        total + ((curr.goodsItem?.value || 0) * (curr?.quantity || 0))
      ), 0)
      .toFixed(3);

    form.setFieldValue([formName, index, 'value'], Number.parseFloat(updatedValue));
  };

  const validateField = () => form.validateFields([[formName, index, 'items']]);

  const handleAddItems = (selectedItems: PackageItem[]) => {
    form.setFieldValue([formName, index, 'items'], selectedItems);

    /** Update weight after update of package */
    handleSideCalcActions(selectedItems);

    triggerValidationAllForm();
    handleOk();
    validateField();
  };

  const handleDeleteItem = (goodsId: string) => {
    const prevVal: PackageItem[] = form.getFieldValue([formName, index, 'items']);

    const newVal: PackageItem[] = (prevVal || [])
      ?.filter((i) => (
        i?.goodsItem?.id !== goodsId
      ));

    form.setFieldValue([formName, index, 'items'], newVal);
    setSelectedProducts((prevState) => prevState
      .filter((pack) => (
        pack?.goodsItem?.id !== goodsId
      )));
    handleSideCalcActions(newVal);
    validateField();
  };

  const isProductsError = form.getFieldError([formName, index, 'items']);

  return (
    <>
      <Form.Item
        label="Products"
        name={[index, 'items']}
        rules={[{ required: props.required, message: 'Please select products' }]}
        // required={props.required}
      >
        {/* <TextArea readOnly style={{ display: 'none' }} /> */}
        {/** There created custom value display component, without any inputs. all data saved in form */}
        <div className={clsx(styles.productsWrapper, { [styles.error]: isProductsError?.length })}>
          <div className={styles.mainContent}>
            {(inputValueWatch || [])
              .map((i) => (
                <div
                  key={i.goodsItem?.id}
                  className={styles.productItem}
                >
                  <span>
                    <b>
                      {i?.quantity}
                      {'x '}
                    </b>
                    {i?.goodsItem?.name}
                  </span>

                  <div
                    role="button"
                    tabIndex={-1}
                    aria-label="delete-button"
                    className={styles.deleteIcon}
                    onClick={() => handleDeleteItem(i?.goodsItem?.id || '')}
                  >
                    <CloseOutlined />
                  </div>
                </div>
              ))}
          </div>
          <Tooltip
            placement="top"
            title="Choose a product from this order."
            className={styles.addonAfter}
          >
            {orderUpdate.loading ? (
              <div>
                <LoadingOutlined />
              </div>
            ) : (
              <PlusOutlined
                disabled={orderUpdate.loading}
                onClick={(e) => {
                  e.preventDefault();
                  showModal();
                }}
              />
            )}
          </Tooltip>
        </div>
      </Form.Item>

      {/* TODO remove if form is okay handles value without Input */}
      {/* <OrderInput
        // options={(inputValueWatch || []).map((val) => ({
        //   label: `${val.quantity}x ${val.goodsItem.name}`,
        //   value: val,
        // }))}
        // selectProps={{
        //   mode: 'multiple',
        //   onClick: () => showModal(),
        //   ...selectProps,
        //   // ...inputProps,
        //   // ...selectProps,
        // }}
        className={styles.inputSelectProduct}
        rest={item}
        label="Products"
        prefix={formName}
        name={name}
        // rules={[validationRules, { required: true, message: <div /> }]}
        rightText="DGA"
        inputProps={{
          addonAfter: (
            <Tooltip
              placement="top"
              title="Choose a product from this order."
            >
              <PlusOutlined
                onClick={(e) => {
                  e.preventDefault();
                  showModal();
                }}
              />
            </Tooltip>
          ),
          maxLength: 35,
          disabled: true,
          ...inputProps,
        }}
        style={{ height: 'auto' }}
        {...props}
      /> */}
      <Modal
        centered
        title="Product Selection"
        open={isModalOpen}
        onCancel={handleCancel}
        okText="Add"
        onOk={() => handleAddItems(selectedProducts)}
        width={1000}
        className={styles.modalTable}
        destroyOnClose
      >
        <ProTable<ClientDataGood>
          actionRef={actionRef}
          columns={columns}
          dataSource={dataSource}
          search={false}
          rowKey="key"
          headerTitle="List of Products"
          pagination={{
            defaultPageSize: 10,
            size: 'default',
            showQuickJumper: true,
            showTotal: undefined,
          }}
          options={{
            density: false,
            // search: { // TODO check mb add Search.
            //   name: 'search',
            // },
            reloadIcon: false,
            setting: false,
          }}
        />
      </Modal>
    </>
  );
}
